import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronLeft, ChevronRight, Megaphone, Plus, X, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Badge from '../../ui/Badge';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { apiClient } from '../../../services/api';
import type { AdminBroadcastAudience, CdnAgent, User } from '../../../types';
import BroadcastMessageBody from '../../BroadcastMessageBody';
import { NumberedPagination, PageHeader, useTimezone, formatInTimezone } from '@kioskgaming/ui';

const BANNER_DISMISS_KEY = 'kiosk_admin_in_app_popup_banner_dismissed';
const TITLE_MAX = 500;
/** Admin + shop/agent send-popup UIs only; backend does not enforce */
const BODY_MAX = 1000;

const AUDIENCE_ORDER: AdminBroadcastAudience[] = [
  'all_users',
  'user_ids',
  'all_agents',
  'agent_ids',
  'all_shops',
  'shop_ids',
];

const AUDIENCE_META: Record<
  AdminBroadcastAudience,
  { label: string; description: string; wideWarning?: string }
> = {
  all_users: {
    label: 'All players',
    description: 'Every player account on the platform.',
    wideWarning: 'This message will be shown to all players. Use only for important announcements.',
  },
  user_ids: {
    label: 'Selected players',
    description: 'Pick specific players from the list below.',
  },
  all_agents: {
    label: 'All agents',
    description: 'Every agent portal account.',
    wideWarning: 'This message will be shown to all agents.',
  },
  agent_ids: {
    label: 'Selected agents',
    description: 'Pick specific agents from the list below.',
  },
  all_shops: {
    label: 'All shops',
    description: 'Every shop portal account.',
    wideWarning: 'This message will be shown to all shops.',
  },
  shop_ids: {
    label: 'Selected shops',
    description: 'Pick specific shops from the list below.',
  },
};

const AUDIENCE_BADGE_LABEL: Record<AdminBroadcastAudience, string> = {
  all_users: 'All players',
  user_ids: 'Selected players',
  all_agents: 'All agents',
  agent_ids: 'Selected agents',
  all_shops: 'All shops',
  shop_ids: 'Selected shops',
};

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function audienceSummary(a: AdminBroadcastAudience, count: number): string {
  if (a === 'user_ids') return `${count} player${count === 1 ? '' : 's'}`;
  if (a === 'agent_ids') return `${count} agent${count === 1 ? '' : 's'}`;
  if (a === 'shop_ids') return `${count} shop${count === 1 ? '' : 's'}`;
  return AUDIENCE_META[a].label;
}

type HistoryRow = {
  id: string;
  title: string;
  body: string;
  audience: string;
  targetIds: string[];
  targetRecipients?: Array<{ id: string; display: string }>;
  expiresAt: string | null;
  displayMode?: string;
  createdAt: string | null;
  sourceType?: string;
  sourceId?: string | null;
  createdByAdmin: { username: string; email: string | null } | null;
};

function recipientPreviewLine(row: HistoryRow, maxShown = 3): string | null {
  const rec = row.targetRecipients;
  if (!rec?.length) return null;
  const labels = rec.map((r) => r.display);
  if (labels.length <= maxShown) return labels.join(', ');
  return `${labels.slice(0, maxShown).join(', ')} +${labels.length - maxShown} more`;
}

function expiryStatus(expiresAt: string | null): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (expiresAt == null) return { label: 'No expiry', variant: 'secondary' };
  const end = new Date(expiresAt);
  if (Number.isNaN(end.getTime())) return { label: 'Expiry unknown', variant: 'outline' };
  if (end.getTime() <= Date.now()) return { label: 'Expired', variant: 'outline' };
  return { label: 'Active', variant: 'default' };
}

const WIZARD_STEP_LABELS = ['Message', 'Audience', 'Visibility', 'Preview'] as const;
const WIZARD_TOTAL_STEPS = WIZARD_STEP_LABELS.length;

const InAppPopupManagement: React.FC = () => {
  const { timezone } = useTimezone();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<AdminBroadcastAudience>('all_users');
  const [displayMode, setDisplayMode] = useState<'once' | 'sticky_daily'>('once');
  const [expiresAt, setExpiresAt] = useState('');
  const [targetIds, setTargetIds] = useState<Set<string>>(new Set());
  const [targetSearch, setTargetSearch] = useState('');

  const [users, setUsers] = useState<User[]>([]);
  const [agents, setAgents] = useState<CdnAgent[]>([]);
  const [shops, setShops] = useState<
    Array<{ id: string; name: string; email: string; agentId: string; agentName: string | null }>
  >([]);

  const [loadingTargets, setLoadingTargets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(BANNER_DISMISS_KEY) !== '1',
  );

  const [historyPage, setHistoryPage] = useState(1);
  const [history, setHistory] = useState<{
    total: number;
    page: number;
    limit: number;
    items: HistoryRow[];
  } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [historyAudienceFilter, setHistoryAudienceFilter] = useState<'all' | AdminBroadcastAudience>('all');
  const [historyChannelFilter, setHistoryChannelFilter] = useState<'all' | 'player' | 'agent' | 'shop'>('all');
  const [historyDaysFilter, setHistoryDaysFilter] = useState<'all' | '7' | '30'>('all');

  const historySectionRef = useRef<HTMLDivElement>(null);

  const needsTargets =
    audience === 'user_ids' || audience === 'agent_ids' || audience === 'shop_ids';

  const loadTargets = useCallback(async () => {
    setLoadingTargets(true);
    try {
      if (audience === 'user_ids') {
        const res = await apiClient.getUsers({ page: 1, limit: 200 } as Parameters<
          typeof apiClient.getUsers
        >[0]);
        setUsers(res.data?.users ?? []);
      } else if (audience === 'agent_ids') {
        const res = await apiClient.getCdnAgents();
        setAgents(res.data ?? []);
      } else if (audience === 'shop_ids') {
        const res = await apiClient.getAdminInAppPopupShopsMeta({ limit: 2000 });
        setShops(res.data?.shops ?? []);
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not load selection list';
      toast.error(msg);
    } finally {
      setLoadingTargets(false);
    }
  }, [audience]);

  useEffect(() => {
    setTargetIds(new Set());
    setTargetSearch('');
  }, [audience]);

  useEffect(() => {
    if (createModalOpen && needsTargets && wizardStep >= 2) {
      void loadTargets();
    }
  }, [createModalOpen, wizardStep, needsTargets, loadTargets]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiClient.getAdminInAppPopups({
        page: historyPage,
        limit: 50,
        ...(historyChannelFilter !== 'all' ? { channel: historyChannelFilter } : {}),
      });
      if (res.data) setHistory(res.data);
    } catch {
      toast.error('Could not load popup history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyChannelFilter]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const targetList = useMemo(() => {
    if (audience === 'user_ids') {
      return users.map((u) => {
        const phone = (u.phone || '').trim();
        const email = (u.email || '').trim();
        const uname = (u.userName || u.name || '').trim();
        const label = phone || email || uname || u.id;
        let sub = '';
        if (phone) {
          sub = email || uname || u.id;
        } else if (email) {
          sub = uname || u.id;
        } else if (uname) {
          sub = u.id;
        }
        if (sub === label) sub = u.id !== label ? u.id : '';
        return { id: u.id, label, sub };
      });
    }
    if (audience === 'agent_ids') {
      return agents.map((a) => ({
        id: a.id,
        label: `${a.name}`,
        sub: a.email,
      }));
    }
    if (audience === 'shop_ids') {
      return shops.map((s) => ({
        id: s.id,
        label: `${s.name}`,
        sub: `${s.agentName || 'Agent'} · ${s.email}`,
      }));
    }
    return [];
  }, [audience, users, agents, shops]);

  const filteredTargets = useMemo(() => {
    const q = targetSearch.trim().toLowerCase();
    if (!q) return targetList;
    return targetList.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.sub.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q),
    );
  }, [targetList, targetSearch]);

  const toggleTarget = (id: string) => {
    setTargetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dismissBanner = () => {
    localStorage.setItem(BANNER_DISMISS_KEY, '1');
    setBannerVisible(false);
  };

  const resetBroadcastForm = useCallback(() => {
    setTitle('');
    setBody('');
    setAudience('all_users');
    setDisplayMode('once');
    setExpiresAt('');
    setTargetIds(new Set());
    setTargetSearch('');
    setWizardStep(1);
  }, []);

  const handleOpenCreateModal = () => {
    resetBroadcastForm();
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (submitting) return;
    setCreateModalOpen(false);
    resetBroadcastForm();
  };

  const validateStep = (step: number): boolean => {
    if (step === 1) {
      if (!title.trim() || !body.trim()) {
        toast.error('Title and body are required');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (needsTargets && targetIds.size === 0) {
        toast.error('Select at least one target');
        return false;
      }
      return true;
    }
    if (step === 3) {
      if (displayMode === 'sticky_daily' && !expiresAt.trim()) {
        toast.error('End date is required for sticky popups');
        return false;
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(wizardStep)) return;
    setWizardStep((s) => Math.min(WIZARD_TOTAL_STEPS, s + 1));
  };

  const goPrev = () => {
    setWizardStep((s) => Math.max(1, s - 1));
  };

  const validateForm = (): boolean => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return false;
    }
    if (needsTargets && targetIds.size === 0) {
      toast.error('Select at least one target');
      return false;
    }
    if (displayMode === 'sticky_daily' && !expiresAt.trim()) {
      toast.error('End date is required for sticky popups');
      return false;
    }
    return true;
  };

  const doSend = async () => {
    setSubmitting(true);
    try {
      const res = await apiClient.createAdminInAppPopup({
        title: title.trim(),
        body: body.trim(),
        audience,
        targetIds: needsTargets ? Array.from(targetIds) : [],
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        displayMode,
      });
      const newId = (res.data as { id?: string } | undefined)?.id ?? null;
      toast.success('Popup sent');
      setCreateModalOpen(false);
      resetBroadcastForm();
      setHistoryPage(1);
      await loadHistory();
      if (newId) {
        setHighlightId(newId);
        window.setTimeout(() => {
          document.getElementById(`in-app-popup-row-${newId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
          window.setTimeout(() => setHighlightId(null), 4000);
        }, 150);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to create popup';
      toast.error(msg, { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const requestSend = () => {
    if (!validateForm()) return;
    void doSend();
  };

  const filteredHistoryItems = useMemo(() => {
    const items = history?.items ?? [];
    let out = items;
    if (historyAudienceFilter !== 'all') {
      out = out.filter((r) => r.audience === historyAudienceFilter);
    }
    if (historyDaysFilter !== 'all') {
      const days = historyDaysFilter === '7' ? 7 : 30;
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      out = out.filter((r) => {
        if (!r.createdAt) return false;
        const t = new Date(r.createdAt).getTime();
        return !Number.isNaN(t) && t >= cutoff;
      });
    }
    return out;
  }, [history?.items, historyAudienceFilter, historyDaysFilter]);

  const toggleHistoryExpand = (id: string) => {
    setExpandedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setQuickExpiry = (mode: '24h' | '7d' | 'clear') => {
    if (mode === 'clear') {
      setExpiresAt('');
      return;
    }
    const d = new Date();
    if (mode === '24h') d.setHours(d.getHours() + 24);
    else d.setDate(d.getDate() + 7);
    setExpiresAt(toDatetimeLocalValue(d));
  };

  const previewTitle = title.trim() || 'Notification title';
  const previewBody = body.trim() || 'Your message will appear here. Add optional links with <link>…</link>.';

  return (
    <div className="space-y-8">
      <PageHeader
        title="In-app popups"
        titleAddon={<Megaphone className="h-8 w-8 text-primary" aria-hidden />}
        description={
          <span className="text-sm leading-relaxed">
            Super admin and system admin only. Messages appear in the recipient app (kiosk, agent, or shop) as in-app notifications.
          </span>
        }
      />

      {bannerVisible ? (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
          <Info className="h-5 w-5 shrink-0 opacity-80" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">High-impact tool</p>
            <p className="mt-0.5 text-blue-900/80 dark:text-blue-200/90">
              Wide audiences (all players, agents, or shops) reach every account. Double-check content
              before sending; body is mostly plain text with optional &lt;link&gt;…&lt;/link&gt; for https URLs.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissBanner}
            className="shrink-0 rounded-md p-1 hover:bg-blue-100 dark:hover:bg-blue-900"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={handleOpenCreateModal}>
          <Plus className="mr-2 h-4 w-4" />
          New popup
        </Button>
      </div>

      {createModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bc-modal-title"
          onClick={() => handleCloseCreateModal()}
        >
          <Card
            className="flex max-h-[min(90vh,880px)] w-full max-w-2xl flex-col overflow-hidden shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="shrink-0 space-y-3 border-b pb-4">
              <div className="flex flex-row items-start justify-between gap-3 space-y-0">
                <div className="min-w-0">
                  <CardTitle id="bc-modal-title" className="text-xl">
                    New popup
                  </CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Step {wizardStep} of {WIZARD_TOTAL_STEPS}:{' '}
                    <span className="font-medium text-foreground">
                      {WIZARD_STEP_LABELS[wizardStep - 1]}
                    </span>
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={() => handleCloseCreateModal()}
                  disabled={submitting}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex gap-1.5" role="tablist" aria-label="Wizard steps">
                {WIZARD_STEP_LABELS.map((label, i) => {
                  const stepNum = i + 1;
                  const active = wizardStep === stepNum;
                  const done = wizardStep > stepNum;
                  return (
                    <div
                      key={label}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        active
                          ? 'bg-primary'
                          : done
                            ? 'bg-primary/40'
                            : 'bg-muted'
                      }`}
                      title={`${stepNum}. ${label}`}
                    />
                  );
                })}
              </div>
            </CardHeader>

            <CardContent className="min-h-0 flex-1 overflow-y-auto py-5">
              {wizardStep === 1 ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Plain text and line breaks. Optional links:{' '}
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">&lt;link&gt;https://…&lt;/link&gt;</code>{' '}
                    or{' '}
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">
                      &lt;link&gt;Label|https://…&lt;/link&gt;
                    </code>{' '}
                    (only <span className="font-medium">http</span> / <span className="font-medium">https</span>).
                  </p>
                  <div>
                    <div className="mb-1 flex items-baseline justify-between gap-2">
                      <label className="text-sm font-medium" htmlFor="bc-title">
                        Title
                      </label>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {title.length}/{TITLE_MAX}
                      </span>
                    </div>
                    <Input
                      id="bc-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                      maxLength={TITLE_MAX}
                      placeholder="Short headline shown in the notification"
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex items-baseline justify-between gap-2">
                      <label className="text-sm font-medium" htmlFor="bc-body">
                        Message
                      </label>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {body.length}/{BODY_MAX}
                      </span>
                    </div>
                    <textarea
                      id="bc-body"
                      className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={body}
                      onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                      maxLength={BODY_MAX}
                      placeholder={`Example: Visit our site <link>https://example.com</link> or <link>Click here|https://example.com</link>`}
                    />
                  </div>
                </div>
              ) : null}

              {wizardStep === 2 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Choose one audience type. Wide options require confirmation before send.
                  </p>
                  <fieldset className="space-y-2">
                    <legend className="sr-only">Audience</legend>
                    {AUDIENCE_ORDER.map((key) => {
                      const meta = AUDIENCE_META[key];
                      const selected = audience === key;
                      return (
                        <label
                          key={key}
                          className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                            selected
                              ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                              : 'border-border hover:bg-muted/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="audience-modal"
                            value={key}
                            checked={selected}
                            onChange={() => setAudience(key)}
                            className="mt-1"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="text-sm font-medium">{meta.label}</span>
                            <p className="mt-0.5 text-xs text-muted-foreground">{meta.description}</p>
                            {selected && meta.wideWarning ? (
                              <p className="mt-2 rounded-md border border-amber-200/80 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                                {meta.wideWarning}
                              </p>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </fieldset>

                  {needsTargets ? (
                    <div className="space-y-2 border-t pt-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <label className="text-sm font-medium">Select targets</label>
                        <span className="text-xs font-medium text-primary">
                          Selected: {targetIds.size}
                        </span>
                      </div>
                      <Input
                        value={targetSearch}
                        onChange={(e) => setTargetSearch(e.target.value)}
                        placeholder="Search by name, email, or ID…"
                      />
                      {loadingTargets ? (
                        <LoadingSpinner />
                      ) : (
                        <div className="max-h-[min(40vh,220px)] space-y-1 overflow-y-auto rounded-md border border-input p-2">
                          {filteredTargets.length === 0 ? (
                            <p className="px-1 py-2 text-sm text-muted-foreground">
                              No matches. Try another search.
                            </p>
                          ) : (
                            filteredTargets.map((t) => (
                              <label
                                key={t.id}
                                className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50"
                              >
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={targetIds.has(t.id)}
                                  onChange={() => toggleTarget(t.id)}
                                />
                                <span className="min-w-0">
                                  <span className="block font-medium leading-tight">{t.label}</span>
                                  <span className="block truncate text-xs text-muted-foreground">
                                    {t.sub}
                                  </span>
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {wizardStep === 3 ? (
                <div className="space-y-3">
                  <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                    <p className="text-sm font-medium">Display behavior</p>
                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name="inapp-display"
                        className="mt-1"
                        checked={displayMode === 'once'}
                        onChange={() => setDisplayMode('once')}
                      />
                      <span>
                        <span className="font-medium">Once per user</span>
                        <span className="block text-xs text-muted-foreground">Hidden after the user taps OK.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name="inapp-display"
                        className="mt-1"
                        checked={displayMode === 'sticky_daily'}
                        onChange={() => setDisplayMode('sticky_daily')}
                      />
                      <span>
                        <span className="font-medium">Sticky (until end date)</span>
                        <span className="block text-xs text-muted-foreground">
                          Shown again on every app open or page reload until the end date. OK hides until the next load;
                          Don&apos;t show again stops it permanently.
                        </span>
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {displayMode === 'sticky_daily'
                      ? 'Set when the campaign ends (required). Times use your browser local timezone (UTC on server).'
                      : 'Hide the message after this time for new loads. Times use '}
                    {displayMode === 'once' ? (
                      <>
                        <strong>your browser&apos;s local timezone</strong> (stored in UTC on the server).
                      </>
                    ) : null}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setQuickExpiry('24h')}>
                      +24 hours
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setQuickExpiry('7d')}>
                      +7 days
                    </Button>
                    {displayMode === 'once' ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => setQuickExpiry('clear')}>
                        No expiry
                      </Button>
                    ) : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="bc-expires">
                      {displayMode === 'sticky_daily' ? 'Campaign end date (required)' : 'Hide message after (optional)'}
                    </label>
                    <Input
                      id="bc-expires"
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      required={displayMode === 'sticky_daily'}
                    />
                  </div>
                </div>
              ) : null}

              {wizardStep === 4 ? (
                <div className="space-y-5">
                  <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
                    <p>
                      <span className="font-medium text-foreground">Audience: </span>
                      <span className="text-muted-foreground">
                        {audienceSummary(audience, targetIds.size)}
                      </span>
                    </p>
                    <p className="mt-2">
                      <span className="font-medium text-foreground">Display: </span>
                      <span className="text-muted-foreground">
                        {displayMode === 'sticky_daily' ? 'Sticky until end date (each reload)' : 'Once per user'}
                      </span>
                    </p>
                    <p className="mt-2">
                      <span className="font-medium text-foreground">Expires: </span>
                      <span className="text-muted-foreground">
                        {expiresAt
                          ? formatInTimezone(expiresAt, timezone)
                          : 'No expiry'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      In-app preview
                    </p>
                    <p className="mb-3 text-xs text-muted-foreground">
                      Approximate look in the shop portal (other apps are similar).
                    </p>
                    <div className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm dark:border-emerald-900 dark:bg-card">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
                        System message
                      </p>
                      <p className="mt-1 text-sm font-semibold text-foreground">{previewTitle}</p>
                      <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                        <BroadcastMessageBody
                          text={previewBody}
                          linkClassName="font-semibold text-emerald-700 underline decoration-emerald-600/70 underline-offset-2 hover:text-emerald-800 break-all dark:text-emerald-400 dark:hover:text-emerald-300"
                        />
                      </p>
                      <div className="mt-3 flex justify-end gap-2 border-t border-border/60 pt-3">
                        <span className="text-[10px] text-muted-foreground">Mark as read</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>

            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t bg-card px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={goPrev}
                disabled={wizardStep <= 1 || submitting}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              {wizardStep < WIZARD_TOTAL_STEPS ? (
                <Button type="button" onClick={goNext} disabled={submitting}>
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => requestSend()}
                  disabled={submitting}
                  loading={submitting}
                >
                  Send popup
                </Button>
              )}
            </div>
          </Card>
        </div>
      ) : null}

      <div ref={historySectionRef}>
        <Card>
          <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-6 space-y-0">
            <div className="min-w-0 flex-1 lg:pr-2">
              <CardTitle>Recent popups</CardTitle>
              <p className="text-sm text-muted-foreground font-normal mt-1">
                Newest first. Expand a row for full body. Recipient (Players / Agents / Shops) filters the
                server list and pagination; audience and date narrow rows on the current page only.
              </p>
            </div>
            <div className="-mx-1 flex flex-nowrap items-center gap-2 overflow-x-auto px-1 pb-0.5 lg:mx-0 lg:shrink-0 lg:overflow-visible lg:px-0 lg:pb-0">
              <select
                className="h-9 shrink-0 rounded-md border border-input bg-background px-2 text-xs min-w-[9rem]"
                value={historyChannelFilter}
                onChange={(e) => {
                  setHistoryChannelFilter(e.target.value as 'all' | 'player' | 'agent' | 'shop');
                  setHistoryPage(1);
                }}
                aria-label="Filter by recipient type"
              >
                <option value="all">All recipients</option>
                <option value="player">Players</option>
                <option value="agent">Agents</option>
                <option value="shop">Shops</option>
              </select>
              <select
                className="h-9 min-w-[8.5rem] shrink-0 rounded-md border border-input bg-background px-2 text-xs"
                value={historyAudienceFilter}
                onChange={(e) =>
                  setHistoryAudienceFilter(e.target.value as 'all' | AdminBroadcastAudience)
                }
              >
                <option value="all">All audiences</option>
                {AUDIENCE_ORDER.map((k) => (
                  <option key={k} value={k}>
                    {AUDIENCE_BADGE_LABEL[k]}
                  </option>
                ))}
              </select>
              <select
                className="h-9 min-w-[6.5rem] shrink-0 rounded-md border border-input bg-background px-2 text-xs"
                value={historyDaysFilter}
                onChange={(e) => setHistoryDaysFilter(e.target.value as 'all' | '7' | '30')}
              >
                <option value="all">Any time</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {historyLoading && !history ? (
              <LoadingSpinner />
            ) : history ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">
                        <span className="sr-only">Expand</span>
                      </TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Audience</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHistoryItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                          No popups match these filters on this page.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredHistoryItems.map((row) => {
                        const exp = expiryStatus(row.expiresAt);
                        const aud = row.audience as AdminBroadcastAudience;
                        let badgeLabel = AUDIENCE_BADGE_LABEL[aud] ?? row.audience;
                        if (
                          row.targetIds?.length &&
                          (row.audience === 'user_ids' ||
                            row.audience === 'agent_ids' ||
                            row.audience === 'shop_ids')
                        ) {
                          badgeLabel = `${badgeLabel} (${row.targetIds.length})`;
                        }
                        const recipientsPreview = recipientPreviewLine(row);
                        const expanded = expandedHistoryIds.has(row.id);
                        const isHi = highlightId === row.id;
                        const modeLabel = row.displayMode === 'sticky_daily' ? 'Sticky' : 'Once';
                        return (
                          <React.Fragment key={row.id}>
                            <TableRow
                              id={`in-app-popup-row-${row.id}`}
                              className={`cursor-pointer ${isHi ? 'bg-primary/10 ring-1 ring-primary/30' : ''}`}
                              onClick={() => toggleHistoryExpand(row.id)}
                            >
                              <TableCell className="align-middle">
                                <button
                                  type="button"
                                  className="p-1 rounded hover:bg-muted"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleHistoryExpand(row.id);
                                  }}
                                  aria-expanded={expanded}
                                >
                                  {expanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </button>
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-xs align-middle">
                                {row.createdAt
                                  ? formatInTimezone(row.createdAt, timezone)
                                  : '—'}
                              </TableCell>
                              <TableCell className="align-middle max-w-[200px]">
                                <div className="font-medium truncate">{row.title}</div>
                                {!expanded ? (
                                  <div className="line-clamp-1 text-xs text-muted-foreground">
                                    <BroadcastMessageBody
                                      text={row.body}
                                      linkClassName="font-medium text-primary underline underline-offset-2"
                                    />
                                  </div>
                                ) : null}
                              </TableCell>
                              <TableCell className="align-middle max-w-[240px]">
                                <Badge variant="secondary" className="font-normal">
                                  {badgeLabel}
                                </Badge>
                                {recipientsPreview ? (
                                  <p
                                    className="mt-1 line-clamp-2 text-xs text-muted-foreground"
                                    title={row.targetRecipients?.map((r) => r.display).join(', ')}
                                  >
                                    {recipientsPreview}
                                  </p>
                                ) : null}
                              </TableCell>
                              <TableCell className="align-middle">
                                <Badge variant="outline" className="font-normal text-xs">
                                  {modeLabel}
                                </Badge>
                              </TableCell>
                              <TableCell className="align-middle">
                                <Badge variant={exp.variant} className="font-normal">
                                  {exp.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs align-middle">
                                {row.sourceType === 'agent' || row.sourceType === 'shop' ? (
                                  <span title={row.sourceId || ''}>
                                    {row.sourceType === 'agent' ? 'Agent' : 'Shop'}{' '}
                                    {row.sourceId ? `(${String(row.sourceId).slice(0, 8)}…)` : ''}
                                  </span>
                                ) : (
                                  row.createdByAdmin?.username || '—'
                                )}
                              </TableCell>
                            </TableRow>
                            {expanded ? (
                              <TableRow className={isHi ? 'bg-primary/5' : 'bg-muted/30'}>
                                <TableCell colSpan={7} className="space-y-4 py-4 text-sm whitespace-pre-wrap">
                                  {row.targetRecipients && row.targetRecipients.length > 0 ? (
                                    <div>
                                      <span className="mb-2 block text-xs font-medium text-muted-foreground">
                                        Recipients ({row.targetRecipients.length})
                                      </span>
                                      <ul className="list-inside list-disc space-y-1 pl-0.5 text-sm">
                                        {row.targetRecipients.map((r) => (
                                          <li key={r.id}>{r.display}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : null}
                                  <div className="whitespace-pre-wrap">
                                    <span className="mb-2 block text-xs font-medium text-muted-foreground">
                                      Full message
                                    </span>
                                    <BroadcastMessageBody
                                      text={row.body}
                                      linkClassName="font-semibold text-primary underline underline-offset-2 hover:opacity-90 break-all"
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Page {history.page} of {Math.max(1, Math.ceil(history.total / history.limit))}
                    {' · '}
                    {history.total} total
                  </span>
                  <NumberedPagination
                    currentPage={historyPage}
                    totalPages={Math.max(1, Math.ceil(history.total / history.limit))}
                    onPageChange={setHistoryPage}
                    variant="admin"
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default InAppPopupManagement;
